import { List, Typography, Button, Badge, Empty, Divider } from 'antd'
import { useNotificationStore, Notification, TgMessage } from '../../store/notificationStore'
import { useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/ru'
import { useTheme } from '../../contexts/ThemeContext'

dayjs.extend(relativeTime)
dayjs.locale('ru')

const { Text } = Typography

export const NotificationList = ({ onClose }: { onClose?: () => void }) => {
    const {
        notifications,
        loading,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        fetchUnreadCount,
        tgMessages,
        clearTgUnread,
        clearAllNotifications,
    } = useNotificationStore()

    const navigate = useNavigate()
    const { theme } = useTheme()
    const isDark = theme === 'dark'

    useEffect(() => {
        fetchNotifications()
    }, [])

    const handleItemClick = async (notification: Notification) => {
        if (!notification.is_read) {
            await markAsRead(notification.id)
            fetchUnreadCount()
        }
        if (notification.link) {
            navigate(notification.link)
            if (onClose) onClose()
        }
    }

    const handleTgClick = (msg: TgMessage) => {
        if (msg.chat_id) {
            const params = new URLSearchParams()
            params.set('chat', String(msg.chat_id))
            if (msg.account_id) params.set('account', String(msg.account_id))
            if (msg.message_id) params.set('messageId', String(msg.message_id))
            if (msg.topic_id) params.set('topicId', String(msg.topic_id))
            navigate(`/chats?${params.toString()}`)
        }
        clearTgUnread()
        if (onClose) onClose()
    }

    const handleClearAll = async () => {
        await clearAllNotifications()
        fetchUnreadCount()
    }

    const getIcon = (type: string) => {
        switch (type) {
            case 'task_assigned': return '📋'
            case 'task_completed': return '✅'
            case 'task_due': return '⏰'
            case 'broadcast_finished': return '📢'
            default: return 'ℹ️'
        }
    }

    const bgObj = {
        header: isDark ? '#1f1f1f' : 'white',
        hover: isDark ? 'rgba(255,255,255,0.08)' : '#f5f5f5',
        unread: isDark ? 'rgba(0,136,204,0.15)' : '#e6f7ff',
        tgUnread: isDark ? 'rgba(0,180,100,0.12)' : '#f0fff4',
        border: isDark ? '#303030' : '#f0f0f0',
        text: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.88)',
        subtext: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)'
    }

    const hasAny = tgMessages.length > 0 || notifications.length > 0

    return (
        <div style={{ width: 350, maxHeight: 520, overflowY: 'auto', background: bgObj.header }}>
            <div style={{
                padding: '12px 16px',
                borderBottom: `1px solid ${bgObj.border}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'sticky',
                top: 0,
                background: bgObj.header,
                zIndex: 1
            }}>
                <Text strong style={{ color: bgObj.text }}>Уведомления</Text>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {notifications.some(n => !n.is_read) && (
                        <Button type="link" size="small" onClick={() => {
                            markAllAsRead()
                            fetchUnreadCount()
                        }}>
                            Прочитать все
                        </Button>
                    )}
                    {hasAny && (
                        <Button type="link" danger size="small" onClick={handleClearAll}>
                            Очистить все
                        </Button>
                    )}
                </div>
            </div>

            {!hasAny && (
                <div style={{ padding: 24, textAlign: 'center' }}>
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={<span style={{ color: bgObj.subtext }}>Нет уведомлений</span>}
                    />
                </div>
            )}

            {/* TG Messages section */}
            {tgMessages.length > 0 && (
                <>
                    <div style={{ padding: '6px 16px 2px', color: bgObj.subtext, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Telegram
                    </div>
                    {tgMessages.map((msg) => (
                        <div
                            key={msg.id}
                            onClick={() => handleTgClick(msg)}
                            style={{
                                padding: '10px 16px',
                                cursor: 'pointer',
                                background: bgObj.tgUnread,
                                borderBottom: `1px solid ${bgObj.border}`,
                                display: 'flex',
                                gap: 10,
                                alignItems: 'flex-start',
                                transition: 'background 0.2s',
                            }}
                            onMouseEnter={e => (e.currentTarget.style.background = bgObj.hover)}
                            onMouseLeave={e => (e.currentTarget.style.background = bgObj.tgUnread)}
                        >
                            <span style={{ fontSize: 20 }}>💬</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: 13, color: bgObj.text }}>{msg.sender}</div>
                                <div style={{ color: bgObj.subtext, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {msg.text}
                                </div>
                                <div style={{ color: bgObj.subtext, fontSize: 11, marginTop: 2 }}>
                                    {msg.date ? dayjs(msg.date).fromNow() : ''}
                                </div>
                            </div>
                        </div>
                    ))}
                </>
            )}

            {/* System notifications section */}
            {notifications.length > 0 && (
                <>
                    {tgMessages.length > 0 && (
                        <div style={{ padding: '6px 16px 2px', color: bgObj.subtext, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            Система
                        </div>
                    )}
                    <List
                        loading={loading}
                        itemLayout="horizontal"
                        dataSource={notifications}
                        renderItem={(item) => (
                            <List.Item
                                style={{
                                    padding: '12px 16px',
                                    cursor: 'pointer',
                                    background: item.is_read ? 'transparent' : bgObj.unread,
                                    transition: 'background 0.3s',
                                    borderBottom: `1px solid ${bgObj.border}`
                                }}
                                onClick={() => handleItemClick(item)}
                                className="notification-item"
                                onMouseEnter={(e) => {
                                    if (item.is_read) e.currentTarget.style.background = bgObj.hover
                                }}
                                onMouseLeave={(e) => {
                                    if (item.is_read) e.currentTarget.style.background = 'transparent'
                                }}
                            >
                                <List.Item.Meta
                                    avatar={<span style={{ fontSize: 24 }}>{getIcon(item.type)}</span>}
                                    title={
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Text strong={!item.is_read} style={{ color: bgObj.text }}>{item.title}</Text>
                                            {!item.is_read && <Badge status="processing" />}
                                        </div>
                                    }
                                    description={
                                        <div>
                                            <div style={{ marginBottom: 4, color: bgObj.text }}>{item.message}</div>
                                            <Text style={{ fontSize: 12, color: bgObj.subtext }}>
                                                {dayjs(item.created_at).fromNow()}
                                            </Text>
                                        </div>
                                    }
                                />
                            </List.Item>
                        )}
                    />
                </>
            )}
        </div>
    )
}
